import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate, openAttachment } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  GraduationCap,
  FileText,
  Image,
  Download,
  Calendar,
  Users,
  BookOpen,
  CheckCircle,
  X,
  AlertTriangle
} from 'lucide-react';

const InstructorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [instructor, setInstructor] = useState(null);
  const [classes, setClasses] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [instructorRes, classesRes, centersRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/instructors/${id}`),
          axios.get(`${API_URL}/classes?page_size=1000`),
          axios.get(`${API_URL}/training-centers`),
          axios.get(`${API_URL}/courses`)
        ]);
        
        setInstructor(instructorRes.data);
        setTrainingCenters(centersRes.data);
        setCourses(coursesRes.data);
        
        // Handle paginated response for classes
        const allClasses = classesRes.data.items || classesRes.data;
        // Filter classes where this instructor is assigned, sort by date descending
        const instructorClasses = allClasses
          .filter(c => c.instructor_ids?.includes(id))
          .sort((a, b) => new Date(b.date_start) - new Date(a.date_start));
        setClasses(instructorClasses);
        
      } catch (error) {
        toast.error('Failed to fetch instructor details');
        navigate('/instructors');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate]);

  const getTrainingCenterNames = () => {
    if (!instructor?.training_center_ids?.length) return null;
    return instructor.training_center_ids.map(cid => {
      const center = trainingCenters.find(c => c.id === cid);
      return center?.name;
    }).filter(Boolean).join(', ');
  };

  const getCourseNames = () => {
    if (!instructor?.course_ids?.length) return null;
    return instructor.course_ids.map(cid => {
      const course = courses.find(c => c.id === cid);
      return course?.name;
    }).filter(Boolean).join(', ');
  };

  const getTrainingCenterName = (centerId) => {
    const center = trainingCenters.find(c => c.id === centerId);
    return center?.name || '-';
  };

  // Helper function to get class status (same logic as Classes.jsx)
  const getClassStatus = (cls) => {
    if (cls.certificates_issued) {
      const course = courses.find(c => c.id === cls.course_id);
      const completionDate = cls.date_completed || cls.date_start;
      if (course && completionDate) {
        const expiryDate = new Date(completionDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + (course.expiration_years || 3));
        const now = new Date();
        
        if (expiryDate < now) {
          return 'expired';
        } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
          return 'expiring';
        }
      }
      return 'certified';
    } else if (cls.date_completed) {
      return 'completed';
    }
    return 'in_progress';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (!instructor) {
    return null;
  }

  const trainingCenterNames = getTrainingCenterNames();
  const courseNames = getCourseNames();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="instructor-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/instructors')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Instructor Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#2C9DE1]/10 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-[#2C9DE1]" />
              </div>
              <div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {instructor.first_name} {instructor.last_name}
                </CardTitle>
                <CardDescription className="mt-1">
                  <span className="font-mono bg-slate-100 px-2 py-1 rounded">
                    #{instructor.instructor_number}
                  </span>
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate(`/instructors?edit=${instructor.id}`)}
              data-testid="edit-instructor-btn"
            >
              Edit Instructor
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase">ID Number</p>
                <p className="font-medium font-mono">{instructor.id_number}</p>
              </div>
            </div>
            {instructor.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Work Email</p>
                  <p className="font-medium">{instructor.email}</p>
                </div>
              </div>
            )}
            {instructor.personal_email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Personal Email</p>
                  <p className="font-medium">{instructor.personal_email}</p>
                </div>
              </div>
            )}
            {instructor.mobile && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Mobile</p>
                  <p className="font-medium">{instructor.mobile}</p>
                </div>
              </div>
            )}
            {instructor.telephone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Telephone</p>
                  <p className="font-medium">{instructor.telephone}</p>
                </div>
              </div>
            )}
            {(instructor.city || instructor.country) && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Location</p>
                  <p className="font-medium">
                    {[instructor.city, instructor.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}
            {instructor.address && (
              <div className="flex items-center gap-3 col-span-2">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Address</p>
                  <p className="font-medium">{instructor.address}</p>
                </div>
              </div>
            )}
          </div>

          {/* Training Centers & Courses */}
          <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-700">Training Centers</p>
              </div>
              {trainingCenterNames ? (
                <div className="flex flex-wrap gap-2">
                  {instructor.training_center_ids?.map(cid => {
                    const center = trainingCenters.find(c => c.id === cid);
                    return center ? (
                      <Badge key={cid} variant="secondary">{center.name}</Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No training centers assigned</p>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-700">Courses Qualified to Teach</p>
              </div>
              {courseNames ? (
                <div className="flex flex-wrap gap-2">
                  {instructor.course_ids?.map(cid => {
                    const course = courses.find(c => c.id === cid);
                    return course ? (
                      <Badge key={cid} variant="outline" className="bg-[#2C9DE1]/5 border-[#2C9DE1]/20">
                        {course.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No courses assigned</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attachments */}
      {instructor.attachments?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#2C9DE1]" />
              Documents
            </CardTitle>
            <CardDescription>
              Attached files and documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {instructor.attachments.map((file) => (
                <div 
                  key={file.id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50"
                >
                  {getFileIcon(file.content_type)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.filename}</p>
                    <p className="text-xs text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`download-attachment-${file.id}`}
                    onClick={() => openAttachment(`${API_URL}/instructors/${instructor.id}/attachments/${file.id}`).catch(() => toast.error('Could not open file'))}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Classes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#2C9DE1]" />
            Classes Taught
          </CardTitle>
          <CardDescription>
            {classes.length} class{classes.length !== 1 ? 'es' : ''} (most recent first)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {classes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No classes yet</p>
              <p className="text-sm mt-1">This instructor has not been assigned to any classes</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Course</TableHead>
                  <TableHead className="font-semibold">Training Center</TableHead>
                  <TableHead className="font-semibold">Students</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.map((cls) => (
                  <TableRow 
                    key={cls.id} 
                    className="hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => navigate(`/classes/${cls.id}/edit`)}
                  >
                    <TableCell className="font-medium">{formatDate(cls.date_start)}</TableCell>
                    <TableCell>{cls.course_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Building className="w-4 h-4" />
                        {getTrainingCenterName(cls.training_center_id)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-slate-400" />
                        {cls.student_ids?.length || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const status = getClassStatus(cls);
                        switch (status) {
                          case 'expired':
                            return (
                              <Badge className="bg-red-100 text-red-800">
                                <X className="w-3 h-3 mr-1" />
                                Expired
                              </Badge>
                            );
                          case 'expiring':
                            return (
                              <Badge className="bg-amber-100 text-amber-800">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                &lt;90 to Expire
                              </Badge>
                            );
                          case 'certified':
                            return (
                              <Badge className="bg-emerald-100 text-emerald-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Certified
                              </Badge>
                            );
                          case 'completed':
                            return <Badge className="bg-amber-100 text-amber-800">Completed</Badge>;
                          default:
                            return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InstructorDetail;
